import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search } from 'lucide-react';
import { useUsers } from '../../hooks/useUsers';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { AppUser } from '../../types/admin';

// Deterministic avatar background color per uid so each user gets a consistent color.
const AVATAR_COLORS = ['bg-plum', 'bg-rose', 'bg-midnight', 'bg-slate'];
function avatarColor(uid: string): string {
  const code = uid.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function userInitial(user: AppUser): string {
  return (user.displayName || user.email || '?').charAt(0).toUpperCase();
}

interface PendingAction {
  user: AppUser;
  grant: boolean; // true = grant admin, false = revoke
}

export function ManageUsersPage() {
  const navigate = useNavigate();
  const { users, loading, error, setAdminStatus } = useUsers();
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      u =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [users, query]);

  async function handleConfirm() {
    if (!pending) return;
    setActionError(null);
    try {
      await setAdminStatus(pending.user.uid, pending.grant);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Something went wrong');
    }
  }

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Platform Admin Tools
      </button>

      <h1 className="font-display text-h1 text-midnight mb-1">Manage Platform Users</h1>
      <p className="text-small text-slate mb-6">Grant or revoke Platform Admin access for any user.</p>

      {/* Search */}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none"
        />
        <input
          type="search"
          placeholder="Search by name or email…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="w-full h-11 pl-9 pr-4 rounded-lg border border-stone bg-white
                     text-small text-midnight placeholder:text-slate
                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                     transition-colors duration-150"
        />
      </div>

      {/* Action error banner */}
      {actionError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
          {actionError}
        </div>
      )}

      {/* States */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load users</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-slate">No users match your search.</p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <ul className="flex flex-col gap-3">
          {filtered.map(user => (
            <li
              key={user.uid}
              className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm flex gap-3"
            >
              {/* Avatar */}
              <div
                className={`shrink-0 w-10 h-10 rounded-full ${avatarColor(user.uid)} flex items-center justify-center`}
              >
                <span className="text-white font-display font-bold text-sm">
                  {userInitial(user)}
                </span>
              </div>

              {/* Info + action
                  Mobile: flex-col — name/email stacked, button below right-aligned.
                  md+:    flex-row items-center — name/email left, button right. */}
              <div className="flex-1 min-w-0 flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
                {/* Name + email */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-semibold text-small text-midnight truncate">
                      {user.displayName || user.email}
                    </span>
                    {user.isPlatformAdmin && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-plum text-white text-[11px] font-bold uppercase tracking-wide leading-none">
                        Platform Admin
                      </span>
                    )}
                  </div>
                  <span className="block text-[13px] text-slate truncate leading-none">
                    {user.email}
                  </span>
                </div>

                {/* Action button */}
                <div className="flex justify-end md:shrink-0">
                  {user.isPlatformAdmin ? (
                    <button
                      type="button"
                      onClick={() => { setActionError(null); setPending({ user, grant: false }); }}
                      className="h-9 px-4 rounded-lg border border-rose text-rose text-[13px] font-semibold
                                 hover:bg-rose/5 active:bg-rose/10 transition-colors duration-150 min-w-[120px]"
                    >
                      Revoke Admin
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setActionError(null); setPending({ user, grant: true }); }}
                      className="h-9 px-4 rounded-lg border border-plum text-plum text-[13px] font-semibold
                                 hover:bg-plum/5 active:bg-plum/10 transition-colors duration-150 min-w-[120px]"
                    >
                      Make Admin
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Confirmation modals */}
      <ConfirmModal
        isOpen={pending?.grant === true}
        title="Grant Platform Admin access?"
        message={`${pending?.user.displayName || pending?.user.email} will be able to manage all users and app-wide settings.`}
        confirmLabel="Grant Access"
        variant="primary"
        onConfirm={handleConfirm}
        onClose={() => setPending(null)}
      />
      <ConfirmModal
        isOpen={pending?.grant === false}
        title="Revoke Platform Admin access?"
        message={`${pending?.user.displayName || pending?.user.email} will immediately lose admin privileges.`}
        confirmLabel="Revoke Access"
        variant="danger"
        onConfirm={handleConfirm}
        onClose={() => setPending(null)}
      />
    </div>
  );
}
